import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';

/**
 * MedicationAllergyCheckModal — Phase 3D
 *
 * Props:
 *   open: bool
 *   matches: [{ allergy_id, allergen, severity, reaction, matched_via, matched_term }]
 *   highestSeverity: 'ANAPHYLAXIS' | 'SEVERE' | 'MODERATE' | 'MILD'
 *   onCancel: () => void
 *   onAcknowledge: (reason: string) => Promise<void>
 *
 * Behaviour:
 *   - Reason field disables button until ≥20 chars
 *   - ANAPHYLAXIS severity adds a 3-second delay countdown on the Acknowledge button
 */

const SEV_HEADER_TONE = {
  ANAPHYLAXIS: { bg: '#b91c1c', label: 'ANAPHYLAXIS — DO NOT ADMINISTER WITHOUT CLINICAL AUTHORISATION' },
  SEVERE:      { bg: '#b45309', label: 'SEVERE ALLERGY MATCH' },
  MODERATE:    { bg: '#b45309', label: 'MODERATE ALLERGY MATCH' },
  MILD:        { bg: '#6b7280', label: 'MILD ALLERGY MATCH' },
};

const PILL_TONE = {
  ANAPHYLAXIS: 'bg-red-100 text-red-800 border-red-300',
  SEVERE: 'bg-amber-100 text-amber-800 border-amber-300',
  MODERATE: 'bg-amber-100 text-amber-800 border-amber-300',
  MILD: 'bg-muted text-muted-foreground border-muted',
};

export function MedicationAllergyCheckModal({ open, matches, highestSeverity, onCancel, onAcknowledge }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [delaySecondsLeft, setDelaySecondsLeft] = useState(0);

  const isAnaphylaxis = highestSeverity === 'ANAPHYLAXIS';
  const reasonValid = (reason || '').trim().length >= 20;

  useEffect(() => {
    if (!open) {
      setReason('');
      setSubmitting(false);
      return;
    }
    if (isAnaphylaxis) setDelaySecondsLeft(3);
  }, [open, isAnaphylaxis]);

  useEffect(() => {
    if (delaySecondsLeft <= 0) return;
    const t = setTimeout(() => setDelaySecondsLeft(d => d - 1), 1000);
    return () => clearTimeout(t);
  }, [delaySecondsLeft]);

  const handleAcknowledge = async () => {
    if (!reasonValid || submitting || delaySecondsLeft > 0) return;
    setSubmitting(true);
    try {
      await onAcknowledge(reason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  const headerTone = SEV_HEADER_TONE[highestSeverity] || SEV_HEADER_TONE.MODERATE;
  const ackLabel = submitting ? 'Acknowledging…'
    : (delaySecondsLeft > 0 ? `Wait ${delaySecondsLeft}s…` : 'Acknowledge & proceed');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); }}>
      <DialogContent className="max-w-md" data-testid="allergy-check-modal">
        <div style={{ background: headerTone.bg, color: 'white', padding: '10px 14px', borderRadius: 6, fontWeight: 700, marginBottom: 12, fontSize: 13 }}>
          <AlertTriangle className="h-4 w-4 inline mr-1" />
          {headerTone.label}
        </div>
        <DialogHeader>
          <DialogTitle>Allergy match detected</DialogTitle>
          <DialogDescription>
            This medication matches an active drug allergy on file. You must record a clinical reason for proceeding. This will be audit-logged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          {(matches || []).map((m) => (
            <div key={m.allergy_id} className="border rounded-md p-2 text-sm" data-testid={`allergy-match-${m.allergy_id}`}>
              <div className="flex items-center gap-2">
                <strong>{m.allergen}</strong>
                <Badge variant="outline" className={`text-[10px] ${PILL_TONE[m.severity] || ''}`}>{m.severity}</Badge>
              </div>
              {m.reaction && <div className="text-xs text-muted-foreground mt-1"><strong>Reaction:</strong> {m.reaction}</div>}
              <div className="text-[10px] text-muted-foreground mt-1">
                Matched via <strong>{m.matched_via}</strong>{m.matched_term ? <> on <code className="bg-muted px-1 rounded">{m.matched_term}</code></> : null}
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-1 mt-3">
          <label className="text-sm font-medium">Reason for proceeding (required, ≥20 chars)</label>
          <Textarea
            data-testid="allergy-ack-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Doctor authorised — see clinical note 14:30"
          />
          <p className="text-[10px] text-muted-foreground">{(reason || '').trim().length}/20 characters minimum</p>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onCancel} disabled={submitting} data-testid="allergy-ack-cancel">Cancel</Button>
          <Button
            variant="destructive"
            onClick={handleAcknowledge}
            disabled={!reasonValid || submitting || delaySecondsLeft > 0}
            data-testid="allergy-ack-submit"
          >
            {ackLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MedicationAllergyCheckModal;
